import ScrollModal from "@/components/common/scroll-modal";
import { Heading, Button, TextField } from "@elaniintc/goes-design-system";
import { newParentFlowSchema } from "@/schemas/processes";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { AxiosError } from "axios";

type ApiErrors = {
  name?: string[];
};

interface ApiErrorResponse {
  errors?: ApiErrors;
}

interface NewFlowModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; extension_count: number }) => void;
  isLoading?: boolean;
  error?: AxiosError<ApiErrorResponse>;
  processId: string;
  defaultValues?: {
    name: string;
    extension_count: number;
  };
}

export default function EditParentFlowModal({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  error,
  defaultValues = {
    name: "",
    extension_count: 0,
  },
}: NewFlowModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid },
  } = useForm<{
    name: string;
    extension_count: number;
  }>({
    resolver: zodResolver(newParentFlowSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: defaultValues,
  });

  const handleCloseModal = () => {
    reset(defaultValues);
    onClose();
  };

  useEffect(() => {
    const serverNameErr = error?.response?.data?.errors?.name;
    if (serverNameErr?.length) {
      setError("name", { type: "server", message: serverNameErr[0] });
    }
  }, [error, setError]);

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleCloseModal}
      title="Crear Flujo principal"
      description="Este flujo es iniciado por el tramitadór, al cual todos los flujos secundarios estarán conectados."
      srOnlyDescription={false}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col pb-6"
    >
      <div className="flex py-8 justify-center flex-col items-center">
        <Heading variant="h2" className="!mb-0">
          Editar flujo
        </Heading>
        <p className="text-center text-body font-normal leading-3xl">
          Modifica el nombre y el flujo padre de este flujo.
        </p>
      </div>
      <div className="flex flex-col">
        <form
          onSubmit={handleSubmit((data) => {
            onSubmit(data);
          })}
          className="flex flex-col flex-1 min-h-0 px-4 sm:px-8 gap-8"
        >
          <TextField
            label="Nombre del flujo"
            required
            error={errors.name?.message}
            placeholder="Escribe el nombre del flujo"
            {...register("name")}
          />
          <TextField
            label="Cantidad de prórrogas permitidas para el tramitador"
            required
            type="number"
            error={errors.extension_count?.message}
            placeholder="Escribe el número de prórrogas permitidas"
            {...register("extension_count", {
              valueAsNumber: true,
            })}
            helperText="Estas prórrogas pueden ser aplicadas en nodos que tengan vencimiento."
          />
          <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 gap-3">
            <Button
              variant="tertiary"
              size="sm"
              type="button"
              disabled={isLoading}
              onClick={handleCloseModal}
              className="min-w-[120px] cursor-pointer"
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              size="sm"
              type="submit"
              disabled={!isValid}
              isLoading={isLoading}
              className="min-w-[140px] cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            >
              Aceptar
            </Button>
          </div>
        </form>
      </div>
    </ScrollModal>
  );
}
